import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { MapPin, Sprout, Clock, Home, ChevronRight, ChevronLeft, Check, Sparkles } from 'lucide-react';
import { UpgradeDialog } from '@/components/UpgradeDialog';
import { PlantSelector } from '@/components/PlantSelector';
import { ArchetypeSelector } from '@/components/ArchetypeSelector';

interface Plant {
  id: string;
  name: string;
  category: string;
  description: string | null;
  difficulty: string | null;
  plant_type: string;
}

const UK_ZONES = [
  { value: 'UK-South', label: 'South England', description: 'London, Bristol, Southampton' },
  { value: 'UK-Midlands', label: 'Midlands', description: 'Birmingham, Nottingham, Leicester' },
  { value: 'UK-North', label: 'North England', description: 'Manchester, Leeds, Newcastle' },
  { value: 'UK-Scotland', label: 'Scotland', description: 'Edinburgh, Glasgow, Aberdeen' },
  { value: 'UK-Wales', label: 'Wales', description: 'Cardiff, Swansea, Bangor' },
  { value: 'UK-NI', label: 'Northern Ireland', description: 'Belfast, Derry' },
];

const SETUP_OPTIONS = [
  { value: 'windowsill', label: 'Windowsill', icon: '🪟', description: 'Growing on windowsills and indoors' },
  { value: 'greenhouse', label: 'Greenhouse', icon: '🏡', description: 'Have a greenhouse or polytunnel' },
  { value: 'cold_frame', label: 'Cold Frame', icon: '📦', description: 'Using cold frames for hardening off' },
  { value: 'pots', label: 'Pots / Containers', icon: '🪴', description: 'Plant pots on balconies or patio' },
  { value: 'direct', label: 'Direct / Outdoor Beds', icon: '🌱', description: 'Planting straight into beds or ground' },
];


export default function Onboarding() {
  const { user } = useAuth();
  const { isPro, freePlantLimit, canAddPlant } = useSubscription();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [zone, setZone] = useState('');
  const [weekdayMinutes, setWeekdayMinutes] = useState(20);
  const [weekdayCount, setWeekdayCount] = useState(3);
  const [weekendHoursMin, setWeekendHoursMin] = useState(2);
  const [weekendHoursMax, setWeekendHoursMax] = useState(4);
  const [selectedSetups, setSelectedSetups] = useState<string[]>(['direct']);
  const [archetype, setArchetype] = useState('');
  const [plants, setPlants] = useState<Plant[]>([]);
  const [selectedPlants, setSelectedPlants] = useState<string[]>([]);
  const [establishedPlants, setEstablishedPlants] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {
    supabase.from('plants').select('id, name, category, description, difficulty, plant_type').then(({ data }) => {
      if (data) setPlants(data);
    });
  }, []);

  const toggleSetup = (value: string) => {
    setSelectedSetups(prev => 
      prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]
    );
  };

  const togglePlant = (id: string) => {
    setSelectedPlants(prev => {
      if (prev.includes(id)) {
        // Deselecting — also remove from established
        setEstablishedPlants(es => { const next = new Set(es); next.delete(id); return next; });
        return prev.filter(p => p !== id);
      }
      if (!canAddPlant(prev.length)) {
        setShowUpgrade(true);
        return prev;
      }
      return [...prev, id];
    });
  };

  const toggleEstablished = (id: string) => {
    setEstablishedPlants(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const canProceed = () => {
    switch (step) {
      case 0: return !!zone;
      case 1: return selectedPlants.length > 0;
      case 2: return true;
      case 3: return selectedSetups.length > 0;
      case 4: return !!archetype;
      default: return false;
    }
  };

  const handleFinish = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { error: profileError } = await supabase.from('profiles').update({
        growing_zone: zone,
        gardener_archetype: archetype,
        weekday_minutes: weekdayMinutes,
        weekday_count: weekdayCount,
        weekend_hours_min: weekendHoursMin,
        weekend_hours_max: weekendHoursMax,
        setup_type: selectedSetups.length > 1 ? 'mixed' : (selectedSetups[0] || 'direct'),
        available_setups: selectedSetups,
        onboarding_complete: true,
      } as any).eq('user_id', user.id);

      if (profileError) throw profileError;

      const userPlants = selectedPlants.map(plant_id => ({
        user_id: user.id,
        plant_id,
        status: establishedPlants.has(plant_id) ? 'active' : 'planned',
      }));
      const { error: plantsError } = await supabase.from('user_plants').insert(userPlants);

      if (plantsError) throw plantsError;

      toast.success('Your garden plan is ready! 🌱');
      navigate('/');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    { icon: MapPin, label: 'Location' },
    { icon: Sprout, label: 'Plants' },
    { icon: Clock, label: 'Time' },
    { icon: Home, label: 'Setup' },
    { icon: Sparkles, label: 'Archetype' },
  ];

  return (
    <div className="min-h-screen bg-background botanical-dots p-4 md:p-8 relative overflow-hidden">
      {/* Background blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-primary/[0.03] blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[28rem] h-[28rem] rounded-full bg-garden-sun/[0.04] blur-3xl" />
      </div>

      <div className="mx-auto max-w-2xl relative z-10">
        <div className="mb-8 text-center animate-fade-in">
          <h1 className="text-3xl font-bold text-foreground tracking-tight">Set Up Your Garden</h1>
          <p className="mt-2 text-muted-foreground">Let's personalise your growing plan</p>
        </div>

        {/* Progress */}
        <div className="mb-8 flex justify-center gap-2">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`flex h-10 w-10 items-center justify-center rounded-2xl transition-all duration-300 ${
                i <= step ? 'garden-gradient text-primary-foreground shadow-soft' : 'bg-muted text-muted-foreground'
              }`}>
                {i < step ? <Check className="h-5 w-5" /> : <s.icon className="h-5 w-5" />}
              </div>
              {i < steps.length - 1 && (
                <div className={`h-0.5 w-8 rounded-full transition-all duration-500 ${i < step ? 'bg-primary' : 'bg-muted'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 0: Location */}
        {step === 0 && (
          <Card className="card-organic animate-fade-in">
            <CardHeader>
              <CardTitle>Where do you garden?</CardTitle>
              <CardDescription>This helps us determine the best sowing and planting times for your area</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-2">
              {UK_ZONES.map(z => (
                <button
                  key={z.value}
                  onClick={() => setZone(z.value)}
                  className={`flex items-center gap-4 rounded-xl border p-4 text-left transition-all ${
                    zone === z.value ? 'border-primary bg-primary/5 shadow-soft' : 'border-border/60 hover:border-primary/30 hover:shadow-soft'
                  }`}
                >
                  <MapPin className={`h-5 w-5 ${zone === z.value ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div>
                    <div className="font-semibold text-foreground">{z.label}</div>
                    <div className="text-sm text-muted-foreground">{z.description}</div>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Step 1: Plants */}
        {step === 1 && (
          <Card className="card-organic animate-fade-in">
            <CardHeader>
              <CardTitle>What would you like to grow?</CardTitle>
              <CardDescription>Select all the plants you'd like in your garden this season ({selectedPlants.length} selected)</CardDescription>
            </CardHeader>
            <CardContent>
              <PlantSelector
                plants={plants}
                selectedPlants={selectedPlants}
                establishedPlants={establishedPlants}
                onTogglePlant={togglePlant}
                onToggleEstablished={toggleEstablished}
              />
            </CardContent>
          </Card>
        )}

        {/* Step 2: Time */}
        {step === 2 && (
          <Card className="card-organic animate-fade-in">
            <CardHeader>
              <CardTitle>How much time can you give?</CardTitle>
              <CardDescription>We'll fit tasks into your available time — no overwhelm!</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="space-y-4">
                <label className="text-sm font-medium text-foreground">Weekday gardening time</label>
                <div className="flex items-center gap-4">
                  <Slider
                    value={[weekdayMinutes]}
                    onValueChange={([v]) => setWeekdayMinutes(v)}
                    min={5}
                    max={60}
                    step={5}
                    className="flex-1"
                  />
                  <span className="w-20 text-right font-semibold text-primary">{weekdayMinutes} min</span>
                </div>
              </div>
              <div className="space-y-4">
                <label className="text-sm font-medium text-foreground">How many weekdays?</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      onClick={() => setWeekdayCount(n)}
                      className={`flex h-12 w-12 items-center justify-center rounded-xl border font-semibold transition-all ${
                        weekdayCount === n ? 'border-primary bg-primary text-primary-foreground shadow-soft' : 'border-border/60 text-foreground hover:border-primary/30'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-4">
                <label className="text-sm font-medium text-foreground">Weekend hours ({weekendHoursMin}-{weekendHoursMax} hours)</label>
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <span className="w-12 text-sm text-muted-foreground">Min</span>
                    <Slider
                      value={[weekendHoursMin]}
                      onValueChange={([v]) => { setWeekendHoursMin(v); if (v > weekendHoursMax) setWeekendHoursMax(v); }}
                      min={0.5}
                      max={8}
                      step={0.5}
                      className="flex-1"
                    />
                    <span className="w-16 text-right font-semibold text-primary">{weekendHoursMin}h</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="w-12 text-sm text-muted-foreground">Max</span>
                    <Slider
                      value={[weekendHoursMax]}
                      onValueChange={([v]) => { setWeekendHoursMax(v); if (v < weekendHoursMin) setWeekendHoursMin(v); }}
                      min={0.5}
                      max={8}
                      step={0.5}
                      className="flex-1"
                    />
                    <span className="w-16 text-right font-semibold text-primary">{weekendHoursMax}h</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Setup */}
        {step === 3 && (
          <Card className="card-organic animate-fade-in">
            <CardHeader>
              <CardTitle>What's your growing setup?</CardTitle>
              <CardDescription>This determines which stages of the plant lifecycle we include</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-2">
              {SETUP_OPTIONS.map(s => (
                <button
                  key={s.value}
                  onClick={() => toggleSetup(s.value)}
                  className={`flex items-center gap-4 rounded-xl border p-4 text-left transition-all ${
                    selectedSetups.includes(s.value) ? 'border-primary bg-primary/5 shadow-soft' : 'border-border/60 hover:border-primary/30 hover:shadow-soft'
                  }`}
                >
                  <span className="text-2xl">{s.icon}</span>
                  <div className="flex-1">
                    <div className="font-semibold text-foreground">{s.label}</div>
                    <div className="text-sm text-muted-foreground">{s.description}</div>
                  </div>
                  {selectedSetups.includes(s.value) && <Check className="h-5 w-5 text-primary" />}
                </button>
              ))}
              <p className="text-xs text-muted-foreground text-center mt-2">Select all that apply — we'll optimise for the best method per plant</p>
            </CardContent>
          </Card>
        )}
        {/* Step 4: Archetype */}
        {step === 4 && (
          <Card className="card-organic animate-fade-in">
            <CardHeader>
              <CardTitle>Your Gardener Archetype</CardTitle>
              <CardDescription>Which of these best describes your gardening style right now?</CardDescription>
            </CardHeader>
            <CardContent>
              <ArchetypeSelector selected={archetype} onSelect={setArchetype} />
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="mt-6 flex justify-between">
          <Button
            variant="outline"
            onClick={() => setStep(s => s - 1)}
            disabled={step === 0}
            className="rounded-xl"
          >
            <ChevronLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          {step < 4 ? (
            <Button
              onClick={() => setStep(s => s + 1)}
              disabled={!canProceed()}
              className="garden-gradient rounded-xl shadow-soft hover:shadow-medium transition-all hover:scale-[1.01] active:scale-[0.99]"
            >
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleFinish}
              disabled={!canProceed() || saving}
              className="garden-gradient rounded-xl shadow-soft hover:shadow-medium transition-all hover:scale-[1.01] active:scale-[0.99]"
            >
              {saving ? 'Creating your plan...' : 'Create My Garden Plan 🌱'}
            </Button>
          )}
        </div>
      </div>
      <UpgradeDialog open={showUpgrade} onOpenChange={setShowUpgrade} />
    </div>
  );
}
